import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Body1,
  Body2,
  Button,
  Caption1,
  Dialog,
  Input,
  Label,
  Select,
  SelectTrigger,
  Tooltip,
  Trigger,
  UploadIcon,
} from "@activecollab/components";
import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import * as yup from "yup";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";
import { ChangeWorkweekCapacityDialog } from "~/components/Settings/ChangeWorkweekCapacityDialog";
import { WorkWeekHours } from "~/components/Settings/WorkWeekHours";
import { showErrorToast } from "~/components/Toast";
import StepLoader from "~/pages/GettingStarted/StepLoader";
import { useInvoiceTemplate, useUpdateInvoiceTemplate } from "~/repository";
import { useCompleteStepMutation } from "~/repository/useGettingStarted";
import {
  EDIT_COMPANY_SUCCESS,
  EDIT_CONFIG_OPTIONS_SUCCESS,
  EDIT_USERS_WORKWEEK_CAPACITIES_SUCCESS,
} from "~/store/action_types";
import { editCompany } from "~/store/actions/actions.companies";
import { editConfigOptions } from "~/store/actions/actions.configOptions";
import { fetchAllCountries } from "~/store/actions/actions.countriesAndStates";
import { editUsersWorkweekCapacities } from "~/store/actions/actions.users";
import { getOwnersCompany } from "~/store/selectors/selector.companies";
import { getConfigOptions } from "~/store/selectors/selector.configOptions";
import { makeCountriesAsSelectOptionsSelector } from "~/store/selectors/selector.countriesAndStates";
import Angie from "~/utils/angie";
import { shortDayNames } from "~/utils/date";
import upload from "~/utils/upload";
import { assets_url } from "~/utils/urls";
import { prepareThumbnailUrl, validateWorkWeekHours } from "~/utils/utils";
import styles from "../People/Invitation/AcceptInvitationPage.module.css";

export const validationSchema = yup.object().shape({
  company_name: yup.string().required(),
});

const BusinessInfoDialog = ({ open, onClose, isAlreadyCompleted }) => {
  const { t } = useTranslation();
  const uploadRef = useRef(null);
  const formikRef = useRef();
  const dispatch = useDispatch();
  const [openWorkweekCapacityDialog, setOpenWorkweekCapacityDialog] = useState(false);
  const [addedDays, setAddedDays] = useState([]);
  const [removedDays, setRemovedDays] = useState([]);
  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();
  const { data: invoiceTemplate, isFetched: invoiceTemplateIsFetched, refetch } = useInvoiceTemplate();

  const ownerCompany = useSelector(getOwnersCompany);
  const configOptions = useSelector(getConfigOptions);

  const updateMutation = useUpdateInvoiceTemplate();
  const completeStepMutation = useCompleteStepMutation();

  const getCountriesAsSelectOptionsSelector = useMemo(() => {
    return makeCountriesAsSelectOptionsSelector();
  }, []);

  const countriesAsSelectOptionsSelector = useSelector((state) => getCountriesAsSelectOptionsSelector(state));
  useEffect(() => {
    if (!countriesAsSelectOptionsSelector.length) {
      dispatch(fetchAllCountries());
    }
  }, [countriesAsSelectOptionsSelector.length, dispatch]);

  const onSubmit = useCallback(
    (values) => {
      const promises = [];
      const initialValues = formikRef.current.initialValues;

      if (values.uploaded_logo) {
        promises.push({
          key: "logo",
          promise: upload(values.uploaded_logo)(new XMLHttpRequest()).then(async (r) => {
            if (r?.code) {
              await updateMutation.mutateAsync({ uploaded_logo_code: r.code });
              return refetch();
            }
          }),
        });
      }

      if (initialValues.company_name !== values.company_name || initialValues.country !== values.country) {
        const attrs = {};
        if (initialValues.company_name !== values.company_name) {
          attrs.name = values?.company_name;
        }

        if (initialValues.country !== values.country) {
          attrs.country = values.country;
        }

        promises.push({
          key: "company",
          promise: dispatch(editCompany(ownerCompany?.id, attrs)),
        });
      }

      const configOptionsForUpdate = {};

      if (initialValues.time_first_week_day !== values.time_first_week_day) {
        configOptionsForUpdate.time_first_week_day = values.time_first_week_day;
      }

      if (!isEqual(values.initial_time_workdays, values.time_workdays)) {
        const changed_capacities = {};
        values.initial_weekly_worker_capacity.forEach((v, i) => {
          if (values.weekly_worker_capacity[i] !== v) {
            changed_capacities[i] = values.weekly_worker_capacity[i];
          }
        });
        promises.push({
          key: "workweek",
          promise: dispatch(
            editUsersWorkweekCapacities(values.time_workdays, values.weekly_worker_capacity, changed_capacities)
          ),
        });
      } else {
        if (!isEqual(values.weekly_worker_capacity, values.initial_weekly_worker_capacity)) {
          configOptionsForUpdate.weekly_worker_capacity = values.weekly_worker_capacity;
        }
      }

      if (Object.keys(configOptionsForUpdate).length > 0) {
        promises.push({
          key: "configOptions",
          promise: dispatch(editConfigOptions(configOptionsForUpdate)),
        });
      }

      promises.push({
        key: "completeStep",
        promise: completeStepMutation.mutateAsync("getting_started_setup_organization"),
      });

      return Promise.all(promises.map((p) => p.promise))
        .then((responses) => {
          const data = {};

          responses.forEach((r, i) => {
            data[promises[i].key] = r;
          });

          let error = false;

          if (data.logo && !data.logo.status === "success") {
            error = true;
          }

          if (data.company && data.company.type !== EDIT_COMPANY_SUCCESS) {
            error = true;
          }

          if (data.workweek && data.workweek.type !== EDIT_USERS_WORKWEEK_CAPACITIES_SUCCESS) {
            error = true;
          }

          if (data.configOptions && data.configOptions.type !== EDIT_CONFIG_OPTIONS_SUCCESS) {
            error = true;
          }

          if (data.completeStep && !data.completeStep.is_ok) {
            error = true;
          }

          if (!error) {
            Angie.logger.info(
              "Getting started step completed.",
              { step: "organization_setup", reopen: isAlreadyCompleted ? 1 : 0 },
              true
            );
            return onClose(false);
          }

          return showErrorToast("There was an error. Please try again.");
        })
        .catch(() => {
          showErrorToast("There was an error. Please try again.");
        });
    },
    [completeStepMutation, dispatch, isAlreadyCompleted, onClose, ownerCompany?.id, refetch, updateMutation]
  );

  const timeWorkDayCollection = {
    0: { id: 0, name: t("Sunday") },
    1: { id: 1, name: t("Monday") },
  };

  const workdaysCollection = useMemo(() => {
    return {
      "1,2,3,4": { id: "1,2,3,4", name: t("Monday to Thursday") },
      "1,2,3,4,5": { id: "1,2,3,4,5", name: t("Monday to Friday") },
      "1,2,3,4,5,6": { id: "1,2,3,4,5,6", name: t("Monday to Saturday") },
      "0,1,2,3,4": { id: "0,1,2,3,4", name: t("Sunday to Thursday") },
      "0,1,2,3,4,5,6": {
        id: "0,1,2,3,4,5,6",
        name: t("Every Day is a Workday"),
      },
    };
  }, [t]);

  const changeWorkdaysSettings = useCallback((id) => {
    formikRef.current.setFieldValue("pre_commit_time_workdays", id.split(","));
    setOpenWorkweekCapacityDialog(true);
    const timeWorkdays = formikRef.current.values.time_workdays.map((d) => `${d}`);

    const addedDays = id
      .split(",")
      .filter((day) => !timeWorkdays.includes(day))
      .map((day) => {
        return {
          label: shortDayNames[day],
          value: Math.max(...formikRef.current.values.weekly_worker_capacity) / 60,
          disabled: false,
          day: +day,
          isValid: true,
        };
      });

    const removedDays = timeWorkdays
      .filter((day) => !id.split(",").includes(day))
      .map((day) => {
        return {
          label: shortDayNames[day],
          value: 0,
          day: +day,
        };
      });

    setAddedDays(addedDays);
    setRemovedDays(removedDays);
  }, []);

  const resolvedContent = useCallback(
    (oldTimeWorkdays, newTImeWorkdays) => {
      const oldTimeWorkdaysLabel = workdaysCollection[oldTimeWorkdays].name.replace("to", "-");
      const newTimeWorkdaysLabel = workdaysCollection[newTImeWorkdays]?.name.replace("to", "-");

      let content = `<div>Your workdays will change from <b>${oldTimeWorkdaysLabel}</b> to <b>${newTimeWorkdaysLabel}</b>.</div>`;

      if (removedDays.length > 0) {
        content =
          content +
          `<div>The daily capacity for the removed days will be set to 0 hours for all members, including those with custom capacities.</div>`;
      }

      if (addedDays.length > 0) {
        content = content + `Enter working hours for new workdays:`;
      }

      return content;
    },
    [addedDays.length, removedDays.length, workdaysCollection]
  );

  const workWeekHoursValidator = useCallback((v) => validateWorkWeekHours(v, 1), []);

  const days = useCallback(() => {
    if (invoiceTemplateIsFetched) {
      const weeklyWorkerCapacity = formikRef.current
        ? formikRef.current.values.weekly_worker_capacity
        : configOptions.weekly_worker_capacity;
      const timeWorkdays = formikRef.current ? formikRef.current.values.time_workdays : configOptions.time_workdays;
      const firstDayOfWeek = formikRef.current
        ? formikRef.current.values.time_first_week_day
        : configOptions.time_first_week_day;
      const workDays = timeWorkdays.map((day) => {
        return +day;
      });
      const days = weeklyWorkerCapacity?.map((capacity, index) => {
        return {
          label: shortDayNames[index],
          value: !workDays.includes(index) ? 0 : capacity / 60,
          disabled: !workDays.includes(index),
          day: index,
          isValid: workWeekHoursValidator(capacity / 60),
        };
      });
      if (firstDayOfWeek) {
        const sunday = days.shift();
        days.push(sunday);
      }

      return days;
    }

    return [];
  }, [
    invoiceTemplateIsFetched,
    configOptions.time_first_week_day,
    configOptions.time_workdays,
    configOptions.weekly_worker_capacity,
    workWeekHoursValidator,
  ]);

  const onWorkWeekChange = useCallback(
    (modifiedDays) => {
      if (
        isEqual(
          modifiedDays.map((day) => day.value),
          days().map((day) => day.value)
        )
      ) {
        return;
      }

      if (
        modifiedDays.some((day) => {
          if (day.disabled) {
            return false;
          }
          return !workWeekHoursValidator(day.value);
        })
      ) {
        return;
      }

      const values = modifiedDays.map((day) => day.value * 60);

      if (formikRef.current.values.time_first_week_day) {
        const sunday = values.pop();
        values.unshift(sunday);
      }

      formikRef.current.setFieldValue("weekly_worker_capacity", values);
    },
    [days, workWeekHoursValidator]
  );

  const onDialogSubmit = useCallback(
    (days) => {
      const capacities = formikRef.current.values.weekly_worker_capacity;
      days.forEach((day) => {
        capacities[day.day] = day.value * 60;
      });
      removedDays.forEach((day) => {
        capacities[day.day] = 0;
      });
      formikRef.current.setFieldValue("weekly_worker_capacity", capacities);
      formikRef.current.setFieldValue("time_workdays", formikRef.current.values.pre_commit_time_workdays);
      formikRef.current.setFieldValue("pre_commit_time_workdays", []);
      setOpenWorkweekCapacityDialog(false);
    },
    [removedDays]
  );

  const onFirstWeekDayChange = useCallback((id) => formikRef.current.setFieldValue("time_first_week_day", id), []);

  const onLogoUpload = useCallback((e) => {
    const imageFile = e.target.files[0];
    if (imageFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        formikRef.current.setFieldValue("uploaded_logo", e.target.files[0]);
        formikRef.current.setFieldValue("logo_url", reader.result);
      };
      reader.readAsDataURL(imageFile);
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="tw-overflow-auto lg:tw-w-[960px]"
    >
      <div className="tw-p-2 sm:tw-p-6 lg:tw-p-4 tw-flex tw-gap-16 tw-w-full tw-justify-between lg:tw-pl-10">
        <div className="tw-flex tw-flex-col tw-w-full lg:tw-min-w-[426px]">
          <Body1
            weight="bold"
            className="tw-mb-4 tw-mt-2 sm:tw-mb-5 lg:tw-mt-4 lg:tw-mb-8"
          >
            {t("Add Your Business Info")}
          </Body1>
          <div className="tw-flex tw-gap-2 lg:tw-gap-4 tw-p-2 sm:tw-p-4 lg:tw-px-4 lg:tw-py-6 tw-bg-primary-200 tw-rounded lg:tw-hidden">
            <img
              src={`${assets_url}/onboarding/images/BorjaGramage.png`}
              alt="Borja Gramage"
              className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-theme-400 tw-shrink-0"
            />
            <div>
              <Body2 italic>
                &quot;
                {t(
                  "Thanks to ActiveCollab, we’ve considerably improved our budget, and the time to adapt to new projects by 80%."
                )}
                &quot;
              </Body2>
              <Body2
                weight="bold"
                className="tw-mt-4"
              >
                Borja Gramage
              </Body2>
              <Body2>Responsible for Organizational Development and People, Arisnova</Body2>
            </div>
          </div>
          {invoiceTemplateIsFetched ? (
            <Formik
              initialValues={{
                uploaded_logo: null,
                logo_url: invoiceTemplate?.logo ? prepareThumbnailUrl(invoiceTemplate?.logo, 512, 512, "scale") : null,
                company_name: ownerCompany?.name,
                country: ownerCompany?.country,
                time_first_week_day: configOptions.time_first_week_day,
                initial_time_first_week_day: configOptions.time_first_week_day,
                time_workdays: [...configOptions.time_workdays],
                initial_time_workdays: [...configOptions.time_workdays],
                pre_commit_time_workdays: [],
                weekly_worker_capacity: [...configOptions.weekly_worker_capacity],
                initial_weekly_worker_capacity: [...configOptions.weekly_worker_capacity],
              }}
              onSubmit={onSubmit}
              innerRef={formikRef}
              validationSchema={validationSchema}
            >
              {({ handleChange, touched, setFieldValue, errors, values, submitCount, submitForm, isSubmitting }) => (
                <Form>
                  <div className="tw-mt-6 lg:tw-mt-0">
                    <div className="tw-flex tw-items-center tw-mb-4">
                      <div className="tw-flex tw-items-center">
                        <div className={classNames("c-avatar tw-mr-4", styles.Avatar)}>
                          <Avatar
                            url={values.logo_url}
                            size={40}
                          />
                          <input
                            type="file"
                            ref={uploadRef}
                            className="tw-hidden"
                            accept="image/*"
                            onChange={onLogoUpload}
                          />
                          <Trigger
                            onClick={() => {
                              if (uploadRef && uploadRef.current) {
                                uploadRef.current.click();
                              }
                            }}
                            className={classNames("", styles.AvatarUpload)}
                          >
                            <Tooltip
                              title={t("Upload")}
                              placement="top"
                            >
                              <UploadIcon
                                width={32}
                                height={32}
                                className="tw-fill-white"
                              />
                            </Tooltip>
                          </Trigger>
                        </div>
                        <div>
                          <Caption1 weight="bold">{t("Upload Company Logo")}</Caption1>
                          <Caption1
                            color="quaternary"
                            weight="regular"
                          >
                            {t("Recommended")} 512x512px, PNG
                          </Caption1>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="tw-flex tw-gap-4 tw-justify-between">
                    <Field name="company_name">
                      {({ field }) => (
                        <div className="tw-flex-1">
                          <Label
                            size="small"
                            htmlFor="company_name"
                            invalid={submitCount > 0 && touched.company_name && errors.company_name}
                          >
                            {t("Company Name")}*
                          </Label>
                          <Input
                            {...field}
                            id="company_name"
                            onChange={handleChange}
                            ref={(el) => (fieldRef.current.company_name = el)}
                            invalid={!!errors.company_name && submitCount > 0}
                            placeholder={t("Company name")}
                            className={classNames("tw-w-auto", {
                              "tw-border-red-500": submitCount > 0 && errors.company_name,
                            })}
                          />
                        </div>
                      )}
                    </Field>
                    <Field name="country">
                      {({ field }) => (
                        <div className="tw-flex-1">
                          <Label
                            size="small"
                            htmlFor="company-country"
                            className="tw-mt-4"
                          >
                            {t("Country")}
                          </Label>
                          <Select
                            id="company-country"
                            options={countriesAsSelectOptionsSelector}
                            selected={field?.value}
                            target={
                              <SelectTrigger className="tw-w-full">
                                {field?.value
                                  ? countriesAsSelectOptionsSelector.find((item) => item.id === field?.value)?.name
                                  : "Please select..."}
                              </SelectTrigger>
                            }
                            onChange={(id) => setFieldValue("country", id)}
                            noResultText={t("No result")}
                            forceCloseMenu
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                  <Body2
                    weight="bold"
                    className="tw-mt-8"
                  >
                    {t("Workweek and Daily Capacity")}
                  </Body2>
                  <Body2
                    color="secondary"
                    className="tw-mt-2"
                  >
                    {t(
                      "Set your work schedule, so you can better manage workload and plan capacity. Hours can be customized on a user level."
                    )}
                  </Body2>
                  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-mt-6">
                    <Field name="time_first_week_day">
                      {({ field }) => (
                        <div className="tw-flex-1">
                          <Label
                            size="small"
                            htmlFor="first-week-day"
                          >
                            {t("First day of the week")}
                          </Label>
                          <Select
                            options={Object.values(timeWorkDayCollection)}
                            target={
                              <SelectTrigger className="tw-w-full">
                                {timeWorkDayCollection[field.value]?.name}
                              </SelectTrigger>
                            }
                            onChange={onFirstWeekDayChange}
                            forceCloseMenu
                            disabledInternalSort
                            disableSearch
                            selected={field.value}
                            id="first-week-day"
                          />
                        </div>
                      )}
                    </Field>
                    <Field name="time_workdays">
                      {({ field }) => (
                        <div className="tw-flex-1">
                          <Label
                            size="small"
                            htmlFor="time-workdays"
                          >
                            {t("Workdays")}
                          </Label>
                          <Select
                            selected={field?.value.join(",")}
                            options={Object.values(workdaysCollection)}
                            target={
                              <SelectTrigger className="tw-w-full">
                                {workdaysCollection[field?.value.join(",")]?.name}
                              </SelectTrigger>
                            }
                            onChange={changeWorkdaysSettings}
                            forceCloseMenu
                            disableSearch
                            disabledInternalSort
                            keepSameOptionsOrder
                            id="time-workdays"
                          />
                        </div>
                      )}
                    </Field>
                  </div>
                  <Field name="weekly_worker_capacity">
                    {() => (
                      <div className="tw-mt-6">
                        <WorkWeekHours
                          days={days()}
                          onBlur={onWorkWeekChange}
                          isValid={workWeekHoursValidator}
                          minValue={1}
                          className="tw-flex-wrap"
                        />
                      </div>
                    )}
                  </Field>
                  <Caption1
                    className="tw-mt-1"
                    color="tertiary"
                  >
                    {t("Values must end with .00, .25, .5, or .75")}
                  </Caption1>
                  <Dialog.ContentDivider className="tw-mt-6" />
                  <Dialog.Actions className="tw-p-0 tw-mt-8">
                    <Button
                      type="button"
                      variant="primary"
                      className="tw-mr-3"
                      disabled={isSubmitting}
                      onClick={() => {
                        focusFirstInvalidField(errors, false);
                        submitForm();
                      }}
                    >
                      {t("Save")}
                    </Button>
                    <Button
                      type="button"
                      onClick={onClose}
                      variant="secondary"
                      disabled={isSubmitting}
                    >
                      {t("Cancel")}
                    </Button>
                  </Dialog.Actions>
                  <ChangeWorkweekCapacityDialog
                    open={openWorkweekCapacityDialog}
                    onClose={() => setOpenWorkweekCapacityDialog(false)}
                    days={addedDays}
                    handleSubmitWorkweekCapacityChange={onDialogSubmit}
                    content={
                      <Body2
                        className={classNames("tw-flex tw-flex-col tw-items-start", {
                          "tw-gap-4": removedDays.length > 0,
                        })}
                        dangerouslySetInnerHTML={{
                          __html: t(
                            resolvedContent(values.time_workdays.join(","), values.pre_commit_time_workdays.join(","))
                          ),
                        }}
                      />
                    }
                  />
                </Form>
              )}
            </Formik>
          ) : (
            <StepLoader className="tw-mt-6 lg:tw-mt-0" />
          )}
        </div>
        <div className="tw-bg-primary-200 tw-p-8 tw-rounded-lg tw-hidden lg:tw-block">
          <Body1 weight="bold">{t("Why this matters")}</Body1>
          <Body2 className="tw-mt-4">
            {t(
              "By centralizing your business information in ActiveCollab, you’ll gain clarity and control over your operations, workflows, and finances. You'll get real-time insights that help you make better, data-driven decisions."
            )}
          </Body2>
          <Body2 className="tw-mt-4">
            {t(
              "With everything stored in a single place, you’ll keep your team aligned, ensure accuracy, and reduce errors. You’ll boost efficiency, streamline processes, and strengthen your client relationships."
            )}
          </Body2>
          <Dialog.ContentDivider className="tw-my-8" />
          <div className="tw-flex tw-gap-4">
            <img
              src={`${assets_url}/onboarding/images/BorjaGramage.png`}
              alt="Borja Gramage"
              className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-theme-400 tw-shrink-0"
            />
            <div>
              <Body2 italic>
                &quot;
                {t(
                  "Thanks to ActiveCollab, we’ve considerably improved our budget, and the time to adapt to new projects by 80%."
                )}
                &quot;
              </Body2>
              <Body2
                weight="bold"
                className="tw-mt-4"
              >
                Borja Gramage
              </Body2>
              <Body2>Responsible for Organizational Development and People, Arisnova</Body2>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
export default BusinessInfoDialog;

BusinessInfoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  isAlreadyCompleted: PropTypes.bool,
};
